type StatusValue = "on" | "off";

type ProviderLike = {
  name: string;
  slug?: string;
  description?: string;
  status: StatusValue;
  recipient_label?: string;
  verify_recipient?: boolean;
  additional_input?: boolean;
  out_of_stock?: boolean;
  additional_input_placeholder?: string;
  additional_input_type?: string;
  additional_input_label?: string;
  additional_input_form_name?: string;
};

type CategoryLike = {
  provider_id: number | string;
  name: string;
  description?: string;
  status: StatusValue;
  recipient_label?: string;
  verify_recipient?: boolean;
  additional_input?: boolean;
  additional_input_placeholder?: string;
  additional_input_type?: string;
  additional_input_label?: string;
  additional_input_form_name?: string;
};

type PlanLike = {
  id: number | string;
  name?: string;
  message?: string;
  status: StatusValue;
  code?: string;
  countries?: number[] | string[];
  source?: string;
  wallet_price?: number | string;
  api_price?: number | string;
  atm_price?: number | string;
  point_bonus?: number | string;
  discount?: number | string;
  referral_eligible?: boolean | number | string;
  referral_amount?: number | string;
  price_type?: string;
  out_of_stock?: boolean;
  price_currency_id?: number | string;
  image_url?: string;
  index?: number | string;
  provider_id?: number | string;
  sub_category_id?: number | string;
  category_id?: number | string;
  network?: string;
  disco?: string;
  type?: string;
  category?: {
    id?: number | string;
    provider_id?: number | string;
    name?: string;
    provider?: {
      name?: string;
    };
  };
  sub_category?: {
    id?: number | string;
    name?: string;
  };
  [key: string]: unknown;
};

const appendValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;

  if (Array.isArray(value)) {
    formData.append(key, JSON.stringify(value.map((item) => Number(item))));
    return;
  }

  if (typeof value === "boolean") {
    formData.append(key, value ? "1" : "0");
    return;
  }

  formData.append(key, String(value));
};

export const buildProviderStatusFormData = (
  provider: ProviderLike,
  status: StatusValue,
) => {
  const formData = new FormData();

  appendValue(formData, "name", provider.name);
  appendValue(formData, "slug", provider.slug || "");
  appendValue(formData, "description", provider.description || "");
  appendValue(formData, "status", status);
  appendValue(formData, "recipient_label", provider.recipient_label || "");
  appendValue(formData, "verify_recipient", provider.verify_recipient ?? false);
  appendValue(formData, "additional_input", provider.additional_input ?? false);
  appendValue(formData, "out_of_stock", provider.out_of_stock);
  appendValue(
    formData,
    "additional_input_placeholder",
    provider.additional_input_placeholder || "",
  );
  appendValue(
    formData,
    "additional_input_type",
    provider.additional_input_type || "",
  );
  appendValue(
    formData,
    "additional_input_label",
    provider.additional_input_label || "",
  );
  appendValue(
    formData,
    "additional_input_form_name",
    provider.additional_input_form_name || "",
  );

  return formData;
};

export const buildCategoryStatusFormData = (
  category: CategoryLike,
  status: StatusValue,
) => {
  const formData = new FormData();

  appendValue(formData, "provider_id", category.provider_id);
  appendValue(formData, "name", category.name);
  appendValue(formData, "description", category.description || "");
  appendValue(formData, "status", status);
  appendValue(formData, "recipient_label", category.recipient_label || "");
  appendValue(formData, "verify_recipient", category.verify_recipient ?? false);
  appendValue(formData, "additional_input", category.additional_input ?? false);
  appendValue(
    formData,
    "additional_input_placeholder",
    category.additional_input_placeholder || "",
  );
  appendValue(
    formData,
    "additional_input_type",
    category.additional_input_type || "",
  );
  appendValue(
    formData,
    "additional_input_label",
    category.additional_input_label || "",
  );
  appendValue(
    formData,
    "additional_input_form_name",
    category.additional_input_form_name || "",
  );

  return formData;
};

export const buildPlanStatusFormData = (
  plan: PlanLike,
  status: StatusValue,
) => {
  const formData = new FormData();
  const providerId = plan.provider_id ?? plan.category?.provider_id;
  const subCategoryId =
    plan.sub_category_id ??
    plan.category_id ??
    plan.sub_category?.id ??
    plan.category?.id;

  appendValue(formData, "provider_id", providerId);
  appendValue(formData, "sub_category_id", subCategoryId);
  appendValue(formData, "category_id", subCategoryId);
  appendValue(formData, "network", plan.network);
  appendValue(formData, "disco", plan.disco);
  appendValue(formData, "type", plan.type);
  appendValue(formData, "name", plan.name || "");
  appendValue(formData, "message", plan.message || "");
  appendValue(formData, "status", status);
  appendValue(formData, "code", plan.code || "");
  appendValue(formData, "countries", plan.countries || []);
  appendValue(formData, "source", plan.source || "");
  appendValue(formData, "wallet_price", plan.wallet_price ?? "0");
  appendValue(formData, "out_of_stock", plan.out_of_stock);
  appendValue(formData, "api_price", plan.api_price ?? "0");
  appendValue(formData, "atm_price", plan.atm_price ?? "0");
  appendValue(formData, "discount", plan.discount ?? "0");
  appendValue(formData, "referral_eligible", plan.referral_eligible ?? false);
  appendValue(formData, "referral_amount", plan.referral_amount ?? "0");
  appendValue(formData, "point_bonus", plan.point_bonus ?? "");
  appendValue(formData, "price_type", plan.price_type || "fixed");
  appendValue(formData, "price_currency_id", plan.price_currency_id ?? "");
  appendValue(formData, "index", plan.index ?? 1);

  return formData;
};
